import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { useChat } from "@/lib/chat";
import { notifPermission, notifSupported, requestNotifPermission } from "@/lib/notify";
import { analytics } from "@/lib/analytics";
import { useIsMobile } from "@/lib/device";
import { needsHomeScreen, pushSupported, usePushStatus } from "@/lib/push";
import { usePushToggle } from "./PushSettings";

const KEY = "tc_notif_banner_dismissed";
const PUSH_KEY = "tc_push_banner_dismissed";

const PushBanner = ({ prefs, setModal }) => {
  const st = usePushStatus();
  const { busy, enable } = usePushToggle();
  const [dismissed, setDismissed] = useState(localStorage.getItem(PUSH_KEY) === "1");
  if (dismissed || st.loading || st.subscribed || st.enabled === false || st.permission === "denied" || prefs.notify === "none") return null;
  const homescreen = needsHomeScreen();
  const dismiss = () => { localStorage.setItem(PUSH_KEY, "1"); setDismissed(true); analytics.track("push_banner_dismissed", { category: "notifications" }); };
  const openPrefs = () => { analytics.track("push_banner_prefs_clicked", { category: "notifications" }); setModal({ type: "preferences", tab: "notifications" }); };
  return (
    <div className="tc-notif-banner" data-testid="push-banner">
      <Bell size={16} />
      <div className="tc-notif-banner__text">
        <b>{homescreen ? "Add Total Chat to your Home Screen" : "Get notified on this phone"}</b>
        <span>{homescreen ? "Then turn on notifications for mentions, DMs and huddles. " : "Lock-screen alerts for mentions, DMs and huddle invites. "}<button onClick={openPrefs} data-testid="push-banner-prefs">{homescreen ? "How" : "Preferences"}</button></span>
      </div>
      {!homescreen && <button className="tc-btn tc-btn--primary sm" disabled={busy} onClick={async () => { analytics.track("push_banner_enable_clicked", { category: "notifications" }); if (await enable()) dismiss(); }} data-testid="push-banner-enable">Turn on</button>}
      <button className="tc-iconbtn" onClick={dismiss} title="Dismiss" data-testid="push-banner-dismiss"><X size={14} /></button>
    </div>
  );
};

export const NotificationBanner = () => {
  const { prefs, setModal } = useChat();
  const isMobile = useIsMobile();
  const [perm, setPerm] = useState(notifPermission());
  const [dismissed, setDismissed] = useState(localStorage.getItem(KEY) === "1");
  useEffect(() => { const h = () => setPerm(notifPermission()); window.addEventListener("tc:notif-permission", h); return () => window.removeEventListener("tc:notif-permission", h); }, []);
  if (isMobile && pushSupported()) return <PushBanner prefs={prefs} setModal={setModal} />;
  if (!notifSupported() || perm !== "default" || dismissed || prefs.notify === "none" || prefs.desktop === false) return null;
  const enable = async () => { analytics.track("notification_banner_enable_clicked", { category: "notifications" }); await requestNotifPermission(); };
  const dismiss = () => { localStorage.setItem(KEY, "1"); setDismissed(true); analytics.track("notification_banner_dismissed", { category: "notifications" }); };
  return (
    <div className="tc-notif-banner" data-testid="notification-banner">
      <Bell size={16} />
      <div className="tc-notif-banner__text">
        <b>Turn on desktop notifications</b>
        <span>Get alerted when someone mentions you or sends a DM. <button onClick={() => setModal({ type: "preferences", tab: "notifications" })} data-testid="notification-banner-prefs">Preferences</button></span>
      </div>
      <button className="tc-btn tc-btn--primary sm" onClick={enable} data-testid="notification-banner-enable">Enable</button>
      <button className="tc-iconbtn" onClick={dismiss} title="Dismiss" data-testid="notification-banner-dismiss"><X size={14} /></button>
    </div>
  );
};
